<template>
  <div class="customer-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-account-group-outline</v-icon>
          </div>
          <div>
            <div class="page-title">CUSTOMER DIRECTORY</div>
            <div class="page-subtitle">Manage retail customer profiles, KYC records, credit limits &amp; advance deposits</div>
          </div>
        </div>

        <!-- Right: Action Bar (Search + Refresh + Add Customer) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search by name, phone, PAN, Aadhar..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <v-btn
            icon
            small
            title="Refresh Directory"
            class="refresh-icon-btn"
            @click="getCustomer()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="openAddCustomerModal()"
          >
            <v-icon left small>mdi-plus</v-icon>
            Add Customer
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Customers -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL CUSTOMERS</div>
          <div class="kpi-value">{{ totalCustomersCount }}</div>
          <div class="kpi-hint">Registered client directory</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-account-group-outline</v-icon>
        </div>
      </div>

      <!-- 2. Total Advance Held -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">ADVANCE DEPOSITS</div>
          <div class="kpi-value">₹{{ formatCurrency(totalAdvanceHeld) }}</div>
          <div class="kpi-hint">Total client credit held</div>
        </div>
        <div class="kpi-icon-pill" style="background: #ecfdf5; color: #059669;">
          <v-icon size="20" color="#059669">mdi-wallet-plus-outline</v-icon>
        </div>
      </div>

      <!-- 3. Outstanding Receivables -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">OUTSTANDING DUES</div>
          <div class="kpi-value">₹{{ formatCurrency(totalOutstandingReceivables) }}</div>
          <div class="kpi-hint">Unsettled customer balances</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fff7ed; color: #ea580c;">
          <v-icon size="20" color="#ea580c">mdi-scale-balance</v-icon>
        </div>
      </div>

      <!-- 4. KYC Compliant Profiles -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">KYC COMPLIANT</div>
          <div class="kpi-value">{{ kycVerifiedCount }}</div>
          <div class="kpi-hint">Aadhaar / PAN on file</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fdf4ff; color: #a855f7;">
          <v-icon size="20" color="#a855f7">mdi-shield-check-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Master Data Table / Content Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Data Table View -->
      <div v-if="customerList && customerList.length > 0">
        <v-simple-table class="pro-master-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 55px;">S.No.</th>
                <th class="text-left" style="min-width: 220px;">Customer Profile</th>
                <th class="text-left" style="width: 150px;">Location</th>
                <th class="text-left" style="min-width: 180px;">Identity (KYC)</th>
                <th class="text-right" style="width: 130px;">Credit Limit</th>
                <th class="text-right" style="width: 150px;">Ledger Balance</th>
                <th class="text-right" style="width: 150px;">Advance Balance</th>
                <th class="text-center" style="width: 170px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(value, i) in customerList"
                :key="value._id || i"
                class="master-table-row"
              >
                <td class="text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>
                <td>
                  <div class="d-flex align-center">
                    <div class="customer-avatar mr-3">
                      {{ getInitials(value.customerName) }}
                    </div>
                    <div>
                      <div class="customer-name-text">
                        {{ value.customerName || '—' }}
                      </div>
                      <div class="customer-phone-text">
                        <v-icon x-small color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                        <span>{{ value.mobileNumber || 'No Phone' }}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div v-if="value.city" class="d-flex align-center">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-map-marker-outline</v-icon>
                    <span class="font-weight-medium text-capitalize">{{ value.city }}</span>
                  </div>
                  <div v-else class="text-muted">—</div>
                  <div v-if="value.pinNumber" class="text-caption text-muted">PIN: {{ value.pinNumber }}</div>
                </td>
                <td>
                  <div class="d-flex flex-column" style="gap: 4px;">
                    <div v-if="value.panNo" class="d-inline-flex align-center">
                      <span class="kyc-chip kyc-pan">
                        <strong>PAN:</strong> {{ value.panNo }}
                      </span>
                    </div>
                    <div v-if="value.aadharNo" class="d-inline-flex align-center">
                      <span class="kyc-chip kyc-aadhar">
                        <strong>UID:</strong> {{ value.aadharNo }}
                      </span>
                    </div>
                    <div v-if="!value.panNo && !value.aadharNo" class="text-muted" style="font-size: 11.5px;">
                      Unregistered
                    </div>
                  </div>
                </td>
                <td class="text-right">
                  <span v-if="value.creditLimit && value.creditLimit > 0" class="font-weight-medium" style="color: #475569;">
                    ₹{{ formatCurrency(value.creditLimit) }}
                  </span>
                  <span v-else class="text-muted" style="font-size: 11.5px;">
                    No Limit
                  </span>
                </td>
                <td class="text-right">
                  <div v-if="value.customerBalanceAmount && value.customerBalanceAmount > 0" class="balance-due-pill font-weight-bold">
                    ₹{{ formatCurrency(value.customerBalanceAmount) }}
                  </div>
                  <div v-else class="text-muted">
                    —
                  </div>
                  <!-- Matured gold-scheme savings is a credit the store owes the customer,
                       not a debt - shown separately so it never inflates dues owed. -->
                  <div v-if="value.SchemeMaturedTotalAmount && value.SchemeMaturedTotalAmount > 0" class="font-weight-medium" style="color: #059669; font-size: 11.5px; margin-top: 2px;">
                    +₹{{ formatCurrency(value.SchemeMaturedTotalAmount) }} scheme credit
                  </div>
                </td>
                <td class="text-right">
                  <span v-if="value.advanceAmount && value.advanceAmount > 0" class="advance-active-badge">
                    <v-icon x-small color="#059669" class="mr-1">mdi-cash-check</v-icon>
                    ₹{{ formatCurrency(value.advanceAmount) }}
                  </span>
                  <span v-else class="text-muted font-weight-medium">
                    ₹0.00
                  </span>
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center" style="gap: 4px;">
                    <v-btn
                      icon
                      x-small
                      title="Customer Statement"
                      class="action-icon-btn statement-btn"
                      @click="$router.push('/CustomerStatement?id=' + value._id)"
                    >
                      <v-icon size="15" color="#0284c7">mdi-file-document-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Customer Advance"
                      class="action-icon-btn advance-btn"
                      @click="openAdvanceDialog(value)"
                    >
                      <v-icon size="15" color="#059669">mdi-wallet-plus-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Enlist Gold Scheme"
                      class="action-icon-btn scheme-btn"
                      @click="openAddScheme(value)"
                    >
                      <v-icon size="15" color="#b45309">mdi-gold</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Edit Customer"
                      class="action-icon-btn edit-btn"
                      @click="openEditCustomer(value)"
                    >
                      <v-icon size="15" color="#475569">mdi-pencil-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Delete Customer"
                      class="action-icon-btn delete-btn"
                      @click="openDeleteCustomer(value._id)"
                    >
                      <v-icon size="15" color="#dc2626">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Footer & Pagination -->
        <div class="table-footer-wrap px-4 py-3 d-flex flex-wrap align-center justify-space-between border-t">
          <div class="text-caption text-muted">
            Showing {{ ((page - 1) * 20) + 1 }} to {{ Math.min(page * 20, totalCustomersCount) }} of {{ totalCustomersCount }} customers
          </div>
          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            :total-visible="7"
            color="#9a6b27"
            dense
            class="pro-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-account-search-outline</v-icon>
        </div>
        <div class="empty-title">No customers found</div>
        <div class="empty-subtitle">
          {{ keyword ? 'No customer profiles match your search criteria. Try a different keyword.' : 'Get started by creating your first retail customer profile.' }}
        </div>
        <div class="mt-4" v-if="keyword">
          <v-btn small outlined color="#64748b" @click="keyword = ''; getCustomer();">
            <v-icon left small>mdi-close</v-icon>
            Clear Filter
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- ===== ADD CUSTOMER MODAL ===== -->
    <v-dialog v-model="addcustomerdialogue" max-width="820px" persistent>
      <v-form ref="addcat" v-model="addcat" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Modal Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #9a6b27;">
                <v-icon size="18" color="#9a6b27">mdi-account-plus-outline</v-icon>
              </div>
              <div>
                <div class="modal-title">Create New Customer Profile</div>
                <div class="modal-subtitle">Add retail client information, address, and statutory KYC details</div>
              </div>
            </div>
            <v-btn icon small @click="closeAddCustomerModal()" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Modal Body Form -->
          <div class="px-6 py-4">
            <div class="form-section-title mb-3">Profile &amp; Contact</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <label class="field-label">Customer Name <span class="required-star">*</span></label>
                <v-text-field
                  v-model="newCustomer"
                  placeholder="Full customer name"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Phone Number</label>
                <v-text-field
                  v-model="newContact"
                  placeholder="10-digit mobile number"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="phoneRules"
                  type="number"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Address &amp; Location</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <label class="field-label">Address</label>
                <v-textarea
                  v-model="newAddress"
                  placeholder="Street address or locality"
                  rows="3"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <v-row dense>
                  <v-col cols="12">
                    <label class="field-label">City</label>
                    <v-text-field
                      v-model="newCity"
                      placeholder="City / Town"
                      outlined
                      dense
                      color="#9a6b27"
                      hide-details
                      class="pro-input"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <label class="field-label">Pin Code</label>
                    <v-text-field
                      v-model="newpinNumber"
                      placeholder="6-digit PIN"
                      type="number"
                      outlined
                      dense
                      color="#9a6b27"
                      :rules="pinRules"
                      hide-details="auto"
                      class="pro-input"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Statutory KYC &amp; Financials</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <label class="field-label">PAN Number</label>
                <v-text-field
                  v-model="newCustomerPAN"
                  placeholder="e.g. ABCDE1234F"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="panRules"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Aadhaar Number</label>
                <v-text-field
                  v-model="newCustomerAadhar"
                  placeholder="12-digit Aadhaar"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="aadhaarRules"
                  type="number"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Credit Limit (₹)</label>
                <v-text-field
                  v-model="newCreditLimit"
                  placeholder="0 = No limit enforced"
                  type="number"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Modal Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="closeAddCustomerModal()">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="valid()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-check</v-icon>
              Save Customer
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ===== EDIT CUSTOMER MODAL ===== -->
    <v-dialog v-model="editdialogue" max-width="820px" persistent>
      <v-card class="modal-card rounded-lg overflow-hidden">
        <!-- Modal Header -->
        <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="modal-icon-pill mr-3" style="background: #f1f5f9; color: #475569;">
              <v-icon size="18" color="#475569">mdi-pencil-outline</v-icon>
            </div>
            <div>
              <div class="modal-title">Edit Customer Profile</div>
              <div class="modal-subtitle">Update contact details, KYC identifiers, and credit parameters</div>
            </div>
          </div>
          <v-btn icon small @click="editdialogue = false" class="close-btn">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Modal Body Form -->
        <div class="px-6 py-4">
          <div class="form-section-title mb-3">Profile &amp; Contact</div>
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6">
              <label class="field-label">Customer Name</label>
              <v-text-field
                v-model="curItem.customerName"
                placeholder="Full customer name"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
                class="pro-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="field-label">Phone Number</label>
              <v-text-field
                v-model="curItem.mobileNumber"
                placeholder="Phone number"
                outlined
                dense
                color="#9a6b27"
                :rules="phoneRules"
                hide-details="auto"
                class="pro-input"
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="form-section-title mb-3 mt-3">Address &amp; Location</div>
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6">
              <label class="field-label">Address</label>
              <v-textarea
                v-model="curItem.address"
                placeholder="Street address or locality"
                rows="3"
                outlined
                dense
                color="#9a6b27"
                hide-details
                class="pro-input"
              ></v-textarea>
            </v-col>
            <v-col cols="12" sm="6">
              <v-row dense>
                <v-col cols="12">
                  <label class="field-label">City</label>
                  <v-text-field
                    v-model="curItem.city"
                    placeholder="City / Town"
                    outlined
                    dense
                    color="#9a6b27"
                    hide-details
                    class="pro-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <label class="field-label">Pin Code</label>
                  <v-text-field
                    v-model="curItem.pinNumber"
                    placeholder="PIN Code"
                    outlined
                    dense
                    color="#9a6b27"
                    :rules="pinRules"
                    hide-details="auto"
                    class="pro-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <div class="form-section-title mb-3 mt-3">Statutory KYC &amp; Financials</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <label class="field-label">PAN Number</label>
              <v-text-field
                v-model="curItem.panNo"
                placeholder="PAN"
                outlined
                dense
                color="#9a6b27"
                :rules="panRules"
                hide-details="auto"
                class="pro-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="field-label">Aadhaar Number</label>
              <v-text-field
                v-model="curItem.aadharNo"
                placeholder="Aadhaar"
                outlined
                dense
                color="#9a6b27"
                :rules="aadhaarRules"
                hide-details="auto"
                class="pro-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="field-label">Credit Limit (₹)</label>
              <v-text-field
                v-model="curItem.creditLimit"
                placeholder="0 = No limit"
                type="number"
                outlined
                dense
                color="#9a6b27"
                hide-details
                class="pro-input"
              ></v-text-field>
            </v-col>
          </v-row>
        </div>

        <!-- Modal Actions -->
        <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
          <v-btn text class="btn-cancel" @click="editdialogue = false">Cancel</v-btn>
          <v-btn
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="editCustomer()"
            :loading="appLoading"
          >
            <v-icon left small>mdi-check</v-icon>
            Update Customer
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ===== DELETE CUSTOMER MODAL ===== -->
    <v-dialog v-model="deletedialog" max-width="440px">
      <v-card class="modal-card rounded-lg pa-6">
        <div class="text-center">
          <div class="delete-icon-box mb-3 mx-auto">
            <v-icon size="26" color="#dc2626">mdi-trash-can-outline</v-icon>
          </div>
          <div class="delete-title mb-1">Delete Customer Profile</div>
          <div class="delete-subtitle">
            Are you sure you want to remove this customer? This action will archive the record and cannot be easily undone.
          </div>
        </div>
        <div class="d-flex align-center justify-end mt-6" style="gap: 8px;">
          <v-btn text class="btn-cancel" @click="deletedialog = false">Cancel</v-btn>
          <v-btn
            color="#dc2626"
            dark
            class="text-capitalize elevation-1 rounded-lg"
            @click="deleteCustomer()"
            :loading="appLoading"
          >
            <v-icon left small>mdi-delete</v-icon>
            Confirm Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ===== CUSTOMER ADVANCE DIALOG ===== -->
    <v-dialog v-model="advanceDialog" max-width="760px" persistent>
      <v-card class="modal-card rounded-lg overflow-hidden">
        <!-- Advance Header -->
        <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="modal-icon-pill mr-3" style="background: #ecfdf5; color: #059669;">
              <v-icon size="20" color="#059669">mdi-wallet-outline</v-icon>
            </div>
            <div>
              <div class="modal-title">Customer Advance Management</div>
              <div class="modal-subtitle">
                Client: <strong>{{ advanceCustomerName || 'Customer' }}</strong>
              </div>
            </div>
          </div>
          <v-btn icon small @click="closeAdvanceDialog()" class="close-btn">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Available Balance Pill Hero Banner -->
        <div class="px-6 pt-4 pb-2">
          <div class="advance-hero-card d-flex align-center justify-space-between px-4 py-3 rounded-lg">
            <div class="d-flex align-center">
              <v-icon color="#059669" size="24" class="mr-2">mdi-check-decagram</v-icon>
              <div>
                <div class="text-caption font-weight-bold" style="color: #065f46; letter-spacing: 0.5px;">AVAILABLE ADVANCE BALANCE</div>
                <div class="text-h6 font-weight-bold" style="color: #047857; line-height: 1.2;">
                  ₹{{ formatCurrency(advanceAvailable) }}
                </div>
              </div>
            </div>
            <div class="text-caption text-muted">
              Auto-deducts upon billing settlement
            </div>
          </div>
        </div>

        <!-- Tabs: History / Add / Release -->
        <v-tabs v-model="advanceTab" color="#9a6b27" slider-color="#9a6b27" class="px-6">
          <v-tab class="text-capitalize font-weight-semibold">
            <v-icon left small>mdi-history</v-icon> Ledger History
          </v-tab>
          <v-tab class="text-capitalize font-weight-semibold">
            <v-icon left small>mdi-plus-circle-outline</v-icon> Add Advance
          </v-tab>
          <v-tab class="text-capitalize font-weight-semibold">
            <v-icon left small>mdi-minus-circle-outline</v-icon> Release Advance
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="advanceTab" class="px-6 py-4">
          <!-- TAB 0: HISTORY -->
          <v-tab-item>
            <div v-if="advanceHistory.length > 0">
              <v-simple-table dense class="pro-master-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Date</th>
                      <th class="text-left">Type</th>
                      <th class="text-right">Amount (₹)</th>
                      <th class="text-left">Mode</th>
                      <th class="text-left">Bill No</th>
                      <th class="text-left">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in advanceHistory" :key="i">
                      <td class="text-muted">{{ formatDate(row.create_date) }}</td>
                      <td>
                        <span
                          class="status-chip"
                          :class="row.type === 'Add' ? 'status-add' : row.type === 'Release' ? 'status-release' : 'status-settle'"
                        >
                          {{ row.type }}
                        </span>
                      </td>
                      <td class="text-right font-weight-semibold" :style="{ color: row.type === 'Add' ? '#059669' : '#dc2626' }">
                        {{ row.type === 'Add' ? '+' : '-' }}₹{{ formatCurrency(row.amount) }}
                      </td>
                      <td>
                        <span class="mode-pill">{{ row.paymentMode || '—' }}</span>
                      </td>
                      <td>{{ row.salesId ? row.salesId.billNo : '—' }}</td>
                      <td class="text-muted">{{ row.remarks || '—' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="d-flex justify-end pt-3" v-if="advancePageLength > 1">
                <v-pagination
                  v-model="advancePage"
                  :length="advancePageLength"
                  small
                  color="#9a6b27"
                  class="pro-pagination"
                ></v-pagination>
              </div>
            </div>
            <div v-else class="text-center py-8 text-muted">
              <v-icon size="36" color="#cbd5e1" class="mb-2">mdi-receipt-text-outline</v-icon>
              <div>No advance transactions recorded for this customer.</div>
            </div>
          </v-tab-item>

          <!-- TAB 1: ADD ADVANCE -->
          <v-tab-item>
            <v-row dense>
              <v-col cols="12" sm="6">
                <label class="field-label">Deposit Amount (₹) <span class="required-star">*</span></label>
                <v-text-field
                  v-model="addAdvanceAmount"
                  type="number"
                  placeholder="Enter amount"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Payment Mode <span class="required-star">*</span></label>
                <v-select
                  v-model="addAdvanceMode"
                  :items="paymentModes"
                  placeholder="Select mode"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-select>
              </v-col>
              <v-col cols="12" class="mt-2">
                <label class="field-label">Remarks / Reference</label>
                <v-text-field
                  v-model="addAdvanceRemarks"
                  placeholder="e.g. Booking deposit, Advance against order"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="text-right pt-4">
                <v-btn
                  color="#059669"
                  dark
                  class="text-capitalize elevation-1 rounded-lg"
                  @click="submitAddAdvance()"
                  :loading="advanceLoading"
                >
                  <v-icon left small>mdi-cash-plus</v-icon>
                  Deposit Advance
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 2: RELEASE ADVANCE -->
          <v-tab-item>
            <v-row dense>
              <v-col cols="12" sm="6">
                <label class="field-label">Release Amount (₹) <span class="required-star">*</span></label>
                <v-text-field
                  v-model="releaseAdvanceAmount"
                  type="number"
                  placeholder="Enter release amount"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details="auto"
                  :hint="'Max available: ₹' + formatCurrency(advanceAvailable)"
                  persistent-hint
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Refund Payment Mode <span class="required-star">*</span></label>
                <v-select
                  v-model="releaseAdvanceMode"
                  :items="paymentModes"
                  placeholder="Select mode"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-select>
              </v-col>
              <v-col cols="12" class="mt-2">
                <label class="field-label">Reason for Release</label>
                <v-text-field
                  v-model="releaseAdvanceRemarks"
                  placeholder="e.g. Order cancelled, Customer refund request"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="text-right pt-4">
                <v-btn
                  color="#ea580c"
                  dark
                  class="text-capitalize elevation-1 rounded-lg"
                  @click="submitReleaseAdvance()"
                  :loading="advanceLoading"
                >
                  <v-icon left small>mdi-cash-refund</v-icon>
                  Release Advance
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-dialog>

    <!-- ===== SCHEME ENLISTING MODAL ===== -->
    <v-dialog v-model="addScheme" max-width="780px" persistent>
      <v-form ref="addSchemeForm" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #b45309;">
                <v-icon size="18" color="#b45309">mdi-gold</v-icon>
              </div>
              <div>
                <div class="modal-title">Enlist in Gold Scheme</div>
                <div class="modal-subtitle">Enroll client into active jewellery savings &amp; purchase schemes</div>
              </div>
            </div>
            <v-btn icon small @click="addScheme = false" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <v-row dense>
              <v-col cols="12" sm="6">
                <label class="field-label">Customer Name</label>
                <v-text-field
                  v-model="customerdetails"
                  outlined
                  dense
                  readonly
                  disabled
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Select Scheme <span class="required-star">*</span></label>
                <v-select
                  v-model="goldSchemeName"
                  :items="schemeList"
                  placeholder="Choose scheme"
                  item-text="goldSchemeName"
                  item-value="_id"
                  @input="selectThis(goldSchemeName)"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Scheme Number</label>
                <v-text-field
                  v-model="schemeNumber"
                  placeholder="Enter Scheme Account Number"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Monthly Deposit (₹)</label>
                <v-text-field
                  v-model="mnthlydpst"
                  placeholder="Monthly deposit"
                  disabled
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Start Date</label>
                <v-menu
                  ref="menu1"
                  v-model="menu1"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="fromDate"
                      outlined
                      dense
                      readonly
                      append-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      color="#9a6b27"
                      hide-details
                      class="pro-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fromDate"
                    color="#9a6b27"
                    no-title
                    @input="menu1 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Maturity Date</label>
                <v-text-field
                  v-model="secondDate"
                  placeholder="Auto-calculated maturity"
                  disabled
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="addScheme = false">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="addToScheme()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-plus</v-icon>
              Enroll in Scheme
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalCount: 0,
      jewelleryId: null,
      customerList: [],
      keyword: "",
      schemefilter: "",
      schemeList: [],
      curItem: {},
      curId: null,

      // Form fields for Add Customer
      newCustomer: null,
      newCustomerPAN: null,
      newCustomerAadhar: null,
      newAddress: null,
      newCity: null,
      newContact: null,
      newpinNumber: null,
      newCreditLimit: null,
      addcustomerdialogue: false,
      addcat: false,
      editdialogue: false,
      deletedialog: false,

      // Scheme Enlisting
      addScheme: false,
      customerdetails: "",
      customerId: "",
      goldSchemeName: "",
      schemeNumber: "",
      yrlyintrst: "",
      mnthlydpst: "",
      selectedScheme: [],
      fromDate: new Date().toISOString().slice(0, 10),
      secondDate: "",
      menu1: false,

      // Customer Advance Dialog
      advanceDialog: false,
      advanceTab: 0,
      advanceCustomerId: null,
      advanceCustomerName: "",
      advanceAvailable: 0,
      advanceHistory: [],
      advancePage: 1,
      advancePageLength: 1,
      advanceLoading: false,
      addAdvanceAmount: null,
      addAdvanceMode: null,
      addAdvanceRemarks: "",
      releaseAdvanceAmount: null,
      releaseAdvanceMode: null,
      releaseAdvanceRemarks: "",
      paymentModes: ["Cash", "Card", "Cheque", "QR", "Bank Transfer"],

      // Validation Rules
      rules: {
        required: (value) => !!value || "Required field.",
        counter: (value) => (value && value.length <= 20) || "Max 20 characters",
      },
      phoneRules: [
        (v) => !v || v.length === 10 || "Invalid phone number (must be 10 digits).",
        (v) => !v || /^\d{10}$/.test(v) || "Phone number must contain digits only.",
      ],
      aadhaarRules: [
        (value) => !value || /^\d{12}$/.test(value) || "Invalid Aadhaar number (12 digits).",
      ],
      panRules: [
        (value) =>
          !value || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value) || "Invalid PAN number format (e.g. ABCDE1234F).",
      ],
      pinRules: [
        (value) => !value || /^\d{6}$/.test(value) || "Invalid PIN code (6 digits).",
      ],
    };
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    totalCustomersCount() {
      return this.totalCount || (this.customerList ? this.customerList.length : 0);
    },
    totalAdvanceHeld() {
      if (!this.customerList || !this.customerList.length) return 0;
      return this.customerList.reduce((acc, curr) => acc + (Number(curr.advanceAmount) || 0), 0);
    },
    totalOutstandingReceivables() {
      if (!this.customerList || !this.customerList.length) return 0;
      // Matured gold-scheme savings is a credit owed TO the customer, not a
      // debt they owe - it must never be folded into what customers owe us.
      return this.customerList.reduce((acc, curr) => acc + (Number(curr.customerBalanceAmount) || 0), 0);
    },
    kycVerifiedCount() {
      if (!this.customerList || !this.customerList.length) return 0;
      return this.customerList.filter(
        (c) => (c.panNo && c.panNo.trim()) || (c.aadharNo && c.aadharNo.toString().trim())
      ).length;
    },
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getCustomer();
    },
    schemefilter() {
      this.page = 1;
      this.getCustomer();
    },
    page() {
      this.getCustomer();
    },
    fromDate(newValue) {
      if (newValue) {
        const selectedDate = new Date(newValue);
        const futureDate = new Date(selectedDate.setMonth(selectedDate.getMonth() + 12));
        this.secondDate = futureDate.toISOString().slice(0, 10);
      } else {
        this.secondDate = null;
      }
    },
    advancePage() {
      if (this.advanceDialog) this.loadAdvanceHistory();
    },
  },
  mounted() {
    this.getCustomer();
    this.getSchemeList();
    if (this.fromDate) {
      const selectedDate = new Date(this.fromDate);
      const futureDate = new Date(selectedDate.setMonth(selectedDate.getMonth() + 12));
      this.secondDate = futureDate.toISOString().slice(0, 10);
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return "CU";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    openAddCustomerModal() {
      this.addcustomerdialogue = true;
    },
    closeAddCustomerModal() {
      this.addcustomerdialogue = false;
      this.resetAddForm();
    },
    resetAddForm() {
      this.newCustomer = null;
      this.newCustomerPAN = null;
      this.newCustomerAadhar = null;
      this.newAddress = null;
      this.newCity = null;
      this.newContact = null;
      this.newpinNumber = null;
      this.newCreditLimit = null;
      if (this.$refs.addcat) {
        this.$refs.addcat.resetValidation();
      }
    },
    openEditCustomer(customer) {
      this.curItem = JSON.parse(JSON.stringify(customer));
      this.editdialogue = true;
    },
    openDeleteCustomer(id) {
      this.curId = id;
      this.deletedialog = true;
    },
    openAddScheme(customer) {
      this.customerId = customer._id;
      this.customerdetails = customer.customerName || "";
      this.goldSchemeName = "";
      this.schemeNumber = "";
      this.yrlyintrst = "";
      this.mnthlydpst = "";
      this.fromDate = new Date().toISOString().slice(0, 10);
      this.addScheme = true;
    },
    selectThis(id) {
      const selected = this.schemeList.filter((x) => x._id === id);
      if (selected && selected.length > 0) {
        this.yrlyintrst = selected[0].yearlyIntrest;
        this.mnthlydpst = selected[0].minmonthlydeposit;
      }
    },
    valid() {
      if (!this.newCustomer) {
        this.msg = "Please enter customer name.";
        this.showsnackbar = true;
        return;
      }
      this.AddCustomer();
    },
    getCustomer() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/customer/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          count: 20,
          limit: 20,
          keyword: this.keyword,
          schemeName: this.schemefilter,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.customerList = response.data.data || [];
            const tot = response.data.count || response.data.totalLength || this.customerList.length;
            this.totalCount = tot;
            this.Pagelength = response.data.pages || Math.ceil(tot / 20) || 1;
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    AddCustomer() {
      this.appLoading = true;
      const jId =
        this.jewelleryId ||
        (this.appUser &&
          (this.appUser.jewelleryId ? this.appUser.jewelleryId._id || this.appUser.jewelleryId : null));

      axios({
        url: "/customer/add",
        method: "POST",
        data: {
          customerName: this.newCustomer,
          panNo: this.newCustomerPAN,
          aadharNo: this.newCustomerAadhar,
          address: this.newAddress,
          city: this.newCity,
          mobileNumber: this.newContact,
          pinNumber: this.newpinNumber,
          creditLimit: this.newCreditLimit,
          jewelleryId: jId,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.addcustomerdialogue = false;
            this.getCustomer();
            this.msg = response.data.msg || "Customer added successfully!";
            this.showsnackbar = true;
            this.resetAddForm();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    editCustomer() {
      this.appLoading = true;
      axios({
        url: "/customer/edit",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curItem._id,
          mobileNumber: this.curItem.mobileNumber,
          customerName: this.curItem.customerName,
          city: this.curItem.city,
          aadharNo: this.curItem.aadharNo,
          panNo: this.curItem.panNo,
          address: this.curItem.address,
          pinNumber: this.curItem.pinNumber,
          creditLimit: this.curItem.creditLimit,
        },
      })
        .then((response) => {
          this.editdialogue = false;
          this.appLoading = false;
          this.msg = response.data.msg || "Customer updated successfully!";
          this.showsnackbar = true;
          this.getCustomer();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    deleteCustomer() {
      this.appLoading = true;
      axios({
        url: "/customer/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          this.msg = response.data.msg || "Customer deleted successfully!";
          this.showsnackbar = true;
          this.getCustomer();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    getSchemeList() {
      axios({
        method: "get",
        url: "/goldscheme/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          keyword: this.keyword,
          page: 1,
          count: 50,
        },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.schemeList = response.data.data || [];
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    addToScheme() {
      if (!this.goldSchemeName) {
        this.msg = "Please select a gold scheme.";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        url: "/goldscheme/customer/add",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          customerId: this.customerId,
          goldSchemeId: this.goldSchemeName,
          matureDate: this.secondDate,
          startDate: this.fromDate,
          schemeNumber: this.schemeNumber,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg || "Customer enrolled into scheme!";
          this.showsnackbar = true;
          if (response.data.status === true) {
            this.addScheme = false;
            this.customerdetails = "";
            this.yrlyintrst = "";
            this.fromDate = "";
            this.goldSchemeName = "";
            this.schemeNumber = "";
            this.mnthlydpst = "";
            this.secondDate = "";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    // ========= ADVANCE METHODS =========
    openAdvanceDialog(customer) {
      this.advanceCustomerId = customer._id;
      this.advanceCustomerName = customer.customerName;
      this.advanceAvailable = customer.advanceAmount || 0;
      this.advancePage = 1;
      this.advanceTab = 0;
      this.advanceHistory = [];
      this.advanceDialog = true;
      this.loadAdvanceHistory();
    },
    loadAdvanceHistory() {
      axios({
        method: "get",
        url: "/customeradvance/history",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { customerId: this.advanceCustomerId, page: this.advancePage, limit: 20 },
      })
        .then((response) => {
          if (response.data.status) {
            this.advanceHistory = response.data.data || [];
            this.advanceAvailable = response.data.availableAdvance || 0;
            this.advancePageLength = Math.ceil((response.data.totalLength || 1) / 20);
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          console.error(err);
          this.ServerError = true;
        });
    },
    closeAdvanceDialog() {
      this.advanceDialog = false;
      this.addAdvanceAmount = null;
      this.addAdvanceMode = null;
      this.addAdvanceRemarks = "";
      this.releaseAdvanceAmount = null;
      this.releaseAdvanceMode = null;
      this.releaseAdvanceRemarks = "";
      this.getCustomer();
    },
    submitAddAdvance() {
      if (!this.addAdvanceAmount || parseFloat(this.addAdvanceAmount) <= 0) {
        this.msg = "Please enter a valid amount.";
        this.showsnackbar = true;
        return;
      }
      if (!this.addAdvanceMode) {
        this.msg = "Please select a payment mode.";
        this.showsnackbar = true;
        return;
      }
      this.advanceLoading = true;
      axios({
        method: "post",
        url: "/customeradvance/add",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          customerId: this.advanceCustomerId,
          amount: parseFloat(this.addAdvanceAmount),
          paymentMode: this.addAdvanceMode,
          remarks: this.addAdvanceRemarks,
        },
      })
        .then((response) => {
          this.advanceLoading = false;
          this.msg = response.data.msg || "Advance added successfully!";
          this.showsnackbar = true;
          if (response.data.status) {
            this.addAdvanceAmount = null;
            this.addAdvanceMode = null;
            this.addAdvanceRemarks = "";
            this.advanceTab = 0;
            this.loadAdvanceHistory();
          }
        })
        .catch((err) => {
          this.advanceLoading = false;
          console.error(err);
          this.ServerError = true;
        });
    },
    submitReleaseAdvance() {
      if (!this.releaseAdvanceAmount || parseFloat(this.releaseAdvanceAmount) <= 0) {
        this.msg = "Please enter a valid amount.";
        this.showsnackbar = true;
        return;
      }
      if (parseFloat(this.releaseAdvanceAmount) > this.advanceAvailable) {
        this.msg =
          "Release amount exceeds available advance (₹" +
          this.formatCurrency(this.advanceAvailable) +
          ").";
        this.showsnackbar = true;
        return;
      }
      if (!this.releaseAdvanceMode) {
        this.msg = "Please select a payment mode.";
        this.showsnackbar = true;
        return;
      }
      this.advanceLoading = true;
      axios({
        method: "post",
        url: "/customeradvance/release",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          customerId: this.advanceCustomerId,
          amount: parseFloat(this.releaseAdvanceAmount),
          paymentMode: this.releaseAdvanceMode,
          remarks: this.releaseAdvanceRemarks,
        },
      })
        .then((response) => {
          this.advanceLoading = false;
          this.msg = response.data.msg || "Advance released successfully!";
          this.showsnackbar = true;
          if (response.data.status) {
            this.releaseAdvanceAmount = null;
            this.releaseAdvanceMode = null;
            this.releaseAdvanceRemarks = "";
            this.advanceAvailable = response.data.remainingAdvance || 0;
            this.advanceTab = 0;
            this.loadAdvanceHistory();
          }
        })
        .catch((err) => {
          this.advanceLoading = false;
          console.error(err);
          this.ServerError = true;
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.customer-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.search-input-wrap {
  width: 320px;
}

@media (max-width: 960px) {
  .search-input-wrap {
    width: 100%;
  }
}

.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
}

.search-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.search-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 38px !important;
  width: 38px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.customer-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 11.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.customer-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13.5px;
}

.customer-phone-text {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 1px;
}

.kyc-chip {
  font-family: monospace;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  letter-spacing: 0.3px;
}

.kyc-pan {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.kyc-aadhar {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.balance-due-pill {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-block;
  font-size: 12px;
}

.advance-active-badge {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 12.5px;
  display: inline-flex;
  align-items: center;
}

.action-icon-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  width: 28px !important;
  height: 28px !important;
}

.action-icon-btn:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}

.action-icon-btn.statement-btn:hover {
  background: #f0f9ff !important;
  border-color: #bae6fd !important;
}

.action-icon-btn.advance-btn:hover {
  background: #f0fdf4 !important;
  border-color: #bbf7d0 !important;
}

.action-icon-btn.scheme-btn:hover {
  background: #fefce8 !important;
  border-color: #fef08a !important;
}

.action-icon-btn.delete-btn:hover {
  background: #fef2f2 !important;
  border-color: #fca5a5 !important;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

.text-muted {
  color: #94a3b8;
}

/* Advance Hero Card */
.advance-hero-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.status-chip {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  display: inline-block;
}

.status-add {
  background: #dcfce7;
  color: #15803d;
}

.status-release {
  background: #ffedd5;
  color: #c2410c;
}

.status-settle {
  background: #f3e8ff;
  color: #7e22ce;
}

.mode-pill {
  font-size: 11px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  color: #475569;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

/* Modal Dialogs */
.modal-card {
  background: #ffffff !important;
}

.modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.modal-icon-pill {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-section-title {
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.required-star {
  color: #dc2626;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
}

.btn-cancel {
  border-radius: 8px !important;
  color: #64748b !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.delete-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.delete-subtitle {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  max-width: 360px;
  margin: 0 auto;
}
</style>
